import React, { useCallback, useEffect, useMemo, useState } from "react";
import Big from "big.js";
import classNames from "classnames";
import CloseIcon from "@material-ui/icons/Close";
import { Button, DropdownMultiple, Field, SideDrawer, Text } from "components/commons";
import { InputAmount } from "components/field";
import { useAlert, useApi } from "hooks";
import { updateShiftById } from "apis/shift-records.api";
import locale from "localization";
import { formatAmount } from "utils";
import styles from "./tag-accountability-drawer.module.scss";

const isVarianceEmployee = (employee) => employee?.name?.toLowerCase() === "stocq variance";

const employeeKey = ({ name, label }) => `${name}__${label ?? ""}`;

const toBig = (value) => {
  const number = Number(String(value ?? "").replace(/,/g, ""));
  return Big(Number.isFinite(number) ? number : 0);
};

const TagAccountabilityDrawer = ({
  transaction,
  shiftId,
  employees = [],
  onUpdateAccountability,
  ...drawer
}) => {
  const { addAlert } = useAlert();
  const { transactionPaymentMethodId, transactionNumber, accountability = [], finalAmount = 0 } =
    transaction ?? {};

  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!drawer.active) return;
    setRows(
      (accountability || []).map((entry) => ({
        key: employeeKey(entry),
        name: entry.name,
        label: entry.label,
        amount: String(entry.amount ?? ""),
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawer.active]);

  const employeeOptions = useMemo(() => {
    const seen = new Set();
    return employees
      .filter((employee) => employee?.name && !isVarianceEmployee(employee))
      .reduce((options, employee) => {
        const key = employeeKey(employee);
        if (seen.has(key)) return options;
        seen.add(key);
        return [...options, { value: key, label: employee.name, employee }];
      }, []);
  }, [employees]);

  const target = useMemo(() => Big(finalAmount || 0).round(2), [finalAmount]);
  const totalShouldered = useMemo(
    () => rows.reduce((sum, row) => sum.add(toBig(row.amount)), Big(0)).round(2),
    [rows],
  );
  const difference = target.minus(totalShouldered);

  const progress = target.gt(0)
    ? Math.min(Number(totalShouldered.div(target).times(100).round(0)), 100)
    : 0;

  const status = difference.eq(0)
    ? { text: locale.fullyAccountedFor, className: styles.statusBalanced }
    : difference.gt(0)
    ? {
        text: locale.populate(locale.shortfallAmount, [formatAmount(Number(difference)).trim()]),
        className: styles.statusShortfall,
      }
    : {
        text: locale.populate(locale.overAmount, [formatAmount(Number(difference.abs())).trim()]),
        className: styles.statusOver,
      };

  const onChangeEmployees = useCallback(
    (_, { value = [] }) => {
      setRows((prev) =>
        value.map(({ value: key }) => {
          const existing = prev.find((row) => row.key === key);
          if (existing) return existing;
          const { employee } = employeeOptions.find((option) => option.value === key) ?? {};
          return { key, name: employee?.name, label: employee?.label, amount: "" };
        }),
      );
    },
    [employeeOptions],
  );

  const onChangeAmount = useCallback((key, amount) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, amount } : row)));
  }, []);

  const onRemove = useCallback((key) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  }, []);

  const onSplitEvenly = useCallback(() => {
    setRows((prev) => {
      if (!prev.length) return prev;
      const cents = Number(target.times(100));
      const share = Math.floor(cents / prev.length);
      const remainder = cents - share * prev.length;
      return prev.map((row, index) => ({
        ...row,
        amount: Big(share + (index === 0 ? remainder : 0))
          .div(100)
          .toFixed(2),
      }));
    });
  }, [target]);

  const updateShift = useApi({
    api: updateShiftById,
    handleOwnError: true,
    params: { shiftId },
  });

  const canConfirm =
    Boolean(transactionPaymentMethodId) &&
    rows.length > 0 &&
    rows.every((row) => toBig(row.amount).gte(1)) &&
    difference.eq(0) &&
    !updateShift.loading;

  const handleConfirm = useCallback(async () => {
    const payload = rows.map(({ name, label, amount }) => ({
      name,
      label,
      amount: Number(toBig(amount)),
    }));

    try {
      await updateShift.request({
        paymentMethod: { transactionPaymentMethodId, accountability: payload },
      });
      onUpdateAccountability?.(transactionPaymentMethodId, payload);
      drawer.close?.();
      addAlert({ title: locale.accountabilitySuccessfullyTagged, type: "success" });
    } catch (error) {
      addAlert({
        title: locale.errorWhileTaggingAccountability,
        subtitle: error?.data?.message || error?.message || "Unknown error occurred",
        type: "error",
        interval: 10000,
      });
    }
  }, [rows, updateShift, transactionPaymentMethodId, onUpdateAccountability, drawer, addAlert]);

  return (
    <SideDrawer
      {...drawer}
      title={locale.tagAccountability}
      subtitle={transactionNumber}
      footer={
        <>
          <Button outline onClick={() => drawer.close?.()}>
            {locale.cancel}
          </Button>
          <Button
            primary
            disabled={!canConfirm}
            loading={updateShift.loading}
            onClick={handleConfirm}
          >
            {locale.confirm}
          </Button>
        </>
      }
    >
      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <Text className={styles.summaryLabel}>{locale.finalAmount}</Text>
          <Text className={styles.summaryValue}>{formatAmount(Number(target))}</Text>
        </div>
        <div className={styles.summaryRow}>
          <Text className={styles.summaryLabel}>{locale.amountShouldered}</Text>
          <Text className={styles.summaryValue}>{formatAmount(Number(totalShouldered))}</Text>
        </div>
        <div className={styles.progressTrack}>
          <div
            className={classNames(styles.progressBar, status.className)}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className={styles.summaryRow}>
          <Text className={classNames(styles.status, status.className)}>{status.text}</Text>
          <Text className={styles.progressValue}>{`${progress}%`}</Text>
        </div>
      </div>

      <Field label={locale.accountableEmployee} className={styles.field}>
        <DropdownMultiple
          name="accountableEmployees"
          placeholder={locale.selectEmployee}
          allLabel={locale.employeesSelected}
          allSelectedText={locale.allEmployees}
          options={employeeOptions}
          total={employeeOptions.length}
          value={rows.map(({ key, name }) => ({ value: key, label: name }))}
          isAll={employeeOptions.length > 0 && rows.length === employeeOptions.length}
          onChange={onChangeEmployees}
        />
      </Field>

      {rows.length > 0 && (
        <>
          <div className={styles.listHeader}>
            <Text className={styles.summaryLabel}>{locale.amountShouldered}</Text>
            <Button link onClick={onSplitEvenly}>
              {locale.splitEvenly}
            </Button>
          </div>
          {rows.map((row) => {
            const others = totalShouldered.minus(toBig(row.amount));
            const max = target.minus(others);
            return (
              <div key={row.key} className={styles.employeeRow}>
                <div className={styles.employeeName}>
                  <Text className={styles.summaryValue}>{row.name}</Text>
                  {row.label && <Text className={styles.employeeLabel}>{row.label}</Text>}
                </div>
                <InputAmount
                  className={styles.amountInput}
                  value={row.amount}
                  min={0}
                  max={Number(max.gt(0) ? max : 0)}
                  onChange={(_, { value }) => onChangeAmount(row.key, value)}
                />
                <CloseIcon className={styles.removeIcon} onClick={() => onRemove(row.key)} />
              </div>
            );
          })}
        </>
      )}
    </SideDrawer>
  );
};

export default TagAccountabilityDrawer;
